import {
  Box,
  ClickAwayListener,
  Fade,
  Paper,
  Popper,
  Theme,
  Typography,
} from '@mui/material';
import React, { useState, useCallback } from 'react';
import { OptionType } from 'types';
import { PositionLeg } from 'types/optionsCalculator';
import { formatStrikeIndicatorExpiration } from 'util/optionsCalculator';

// Memoized out-of-view strike indicator component
const OutOfViewStrikeIndicator = React.memo(
  ({
    closestStrike,
    closestLeg,
    allStrikes,
    allLegs,
    theme,
    direction,
    containerWidth,
  }: {
    closestStrike: number;
    closestLeg: PositionLeg;
    allStrikes: number[];
    allLegs: PositionLeg[];
    theme: Theme;
    direction: 'left' | 'right';
    containerWidth: number;
  }) => {
    const [isHovered, setIsHovered] = useState(false);
    const [popperOpen, setPopperOpen] = useState(false);
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

    const handleClick = useCallback(
      (e: React.MouseEvent<HTMLElement>) => {
        e.stopPropagation();
        setAnchorEl(e.currentTarget);
        setPopperOpen(!popperOpen);
      },
      [popperOpen],
    );

    const handleClose = useCallback(() => {
      setPopperOpen(false);
      setAnchorEl(null);
    }, []);

    const offset = allStrikes.length > 1 ? 25 : 20;
    const position = direction === 'left' ? offset : containerWidth - offset;
    const arrowDirection = direction === 'left' ? '◀' : '▶';
    const optionType = closestLeg.type === OptionType.CALL ? 'C' : 'P';

    // Sort strikes for consistent display
    const sortedStrikes = [...allStrikes].sort((a, b) =>
      direction === 'left' ? b - a : a - b,
    );

    return (
      <>
        <Box
          sx={{
            position: 'absolute',
            left: position,
            top: 92,
            transform: 'translateX(-50%)',
            cursor: 'pointer',
            zIndex: 4,
          }}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          onClick={handleClick}
        >
          <Box
            sx={{
              position: 'relative',
              backgroundColor: isHovered
                ? theme.palette.primary.dark
                : theme.palette.primary.main,
              color: theme.palette.primary.contrastText,
              padding: '4px 6px',
              borderRadius: '4px',
              fontSize: '0.65rem',
              fontWeight: 'bold',
              whiteSpace: 'nowrap',
              boxShadow: isHovered ? theme.shadows[3] : theme.shadows[1],
              border: isHovered
                ? `1px solid ${theme.palette.primary.light}`
                : 'none',
              transform: isHovered ? 'scale(1.05)' : 'scale(1)',
              transition: 'all 0.2s ease-in-out',
              display: 'flex',
              alignItems: 'center',
              gap: '2px',
            }}
          >
            {/* Expiration tag for closest strike */}
            {closestLeg.expiration && (
              <Box
                sx={{
                  position: 'absolute',
                  top: -8,
                  ...(direction === 'left' ? { right: -6 } : { left: -6 }),
                  backgroundColor: theme.palette.info.main,
                  color: theme.palette.info.contrastText,
                  fontSize: '0.5rem',
                  fontWeight: 'bold',
                  padding: '1px 3px',
                  borderRadius: '3px',
                  lineHeight: 1,
                  minWidth: '16px',
                  textAlign: 'center',
                  boxShadow: theme.shadows[1],
                  border: `1px solid ${theme.palette.background.paper}`,
                }}
              >
                {formatStrikeIndicatorExpiration(closestLeg.expiration)}
              </Box>
            )}
            {direction === 'left' && (
              <span style={{ fontSize: '0.5rem' }}>{arrowDirection}</span>
            )}
            {closestStrike}
            {optionType}
            {allStrikes.length > 1 && (
              <span style={{ fontSize: '0.5rem', opacity: 0.8 }}>
                +{allStrikes.length - 1}
              </span>
            )}
            {direction === 'right' && (
              <span style={{ fontSize: '0.5rem' }}>{arrowDirection}</span>
            )}
          </Box>
        </Box>

        <Popper
          open={popperOpen && anchorEl !== null}
          anchorEl={anchorEl}
          placement="top"
          transition
          style={{ zIndex: 1300 }}
          disablePortal={false}
          modifiers={[
            {
              name: 'preventOverflow',
              enabled: true,
              options: {
                altAxis: true,
                altBoundary: true,
                tether: true,
                rootBoundary: 'document',
                padding: 8,
              },
            },
            {
              name: 'flip',
              enabled: true,
              options: {
                altBoundary: true,
                rootBoundary: 'document',
                padding: 8,
              },
            },
          ]}
        >
          {({ TransitionProps }) => (
            <ClickAwayListener onClickAway={handleClose}>
              <Fade {...TransitionProps} timeout={200}>
                <Paper
                  sx={{
                    p: 2,
                    backgroundColor: theme.palette.background.paper,
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: 2,
                    boxShadow: theme.shadows[8],
                    minWidth: 200,
                    maxHeight: 300,
                    overflow: 'auto',
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: 'bold', mb: 1 }}
                  >
                    Out of view strikes ({direction})
                  </Typography>
                  <Box
                    sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}
                  >
                    {sortedStrikes.map((strike) => {
                      const legsAtStrike = allLegs.filter(
                        (leg) => leg.strike === strike,
                      );
                      return (
                        <Box
                          key={strike}
                          sx={{
                            borderBottom: `1px solid ${theme.palette.divider}`,
                            pb: 0.5,
                          }}
                        >
                          <Typography
                            variant="caption"
                            sx={{ fontWeight: 'bold' }}
                          >
                            Strike {strike}:
                          </Typography>
                          {legsAtStrike.map((leg) => (
                            <Box key={leg.id} sx={{ ml: 1, mt: 0.5 }}>
                              <Typography variant="caption">
                                {leg.sym} {strike}
                                {leg.type === OptionType.CALL ? 'C' : 'P'} -
                                Qty: {leg.quantity}
                              </Typography>
                              {leg.expiration && (
                                <Typography
                                  variant="caption"
                                  sx={{
                                    display: 'block',
                                    color: 'text.secondary',
                                  }}
                                >
                                  Exp:{' '}
                                  {formatStrikeIndicatorExpiration(
                                    leg.expiration,
                                  )}
                                </Typography>
                              )}
                            </Box>
                          ))}
                        </Box>
                      );
                    })}
                  </Box>
                </Paper>
              </Fade>
            </ClickAwayListener>
          )}
        </Popper>
      </>
    );
  },
);

export default OutOfViewStrikeIndicator;
